import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Tabs, Tab, Badge, Modal, Table, Pagination, InputGroup } from 'react-bootstrap';
import Header from '../common/Header';
import Footer from '../common/Footer';
import MFASetup from '../common/MFASetup';
import { useAdminSEO } from './hooks/useAdminSEO';
import { TicketIcon } from '@heroicons/react/24/solid';

const AdminSEO: React.FC = () => {
  const {
    loading, saving,
    success, error, setFeedback,
    unreadTickets, navigate,
    siteName, siteTagline, siteDescription, setBranding,
    companyName, companyPhone, companyEmail, companyWebsite, companyAddress, businessHours, welcomeMessage, setCompanyInfo,
    metaTitle, metaDescription, metaKeywords, ogTitle, ogDescription, ogImageUrl, canonicalUrl, googleAnalyticsId, gtmContainerId, robotsTxt, setSeoFields,
    facebookUrl, instagramUrl, twitterUrl, linkedinUrl, setSocialLinks,
    footerText, footerCopyright, footerTagline, setFooterFields,
    logosLoading, deleteConfirm, setLogoState,
    getLogosByType, getActiveLogo,
    showUploadModal, uploadType, uploadName, uploadAlt, uploadFile, uploadPreview, uploading, setUploadState,
    fileInputRef,
    handleFileSelect, openUploadModal, handleUploadLogo, handleDeleteLogo, handleToggleActive,
    rateLimits, setRateLimits, rateLimitsSaving, handleSaveRateLimits,
    geoRestriction, setGeoRestriction,
    maintenance, setMaintenance,
    termsState, loadTermsAcceptances, setTermsState,
    handleSave,
  } = useAdminSEO();

  const RATE_LIMIT_LABELS: Record<string, { label: string; desc: string }> = {
    login: { label: 'Login', desc: 'Failed login attempts per IP' },
    registration: { label: 'Registration', desc: 'Registration attempts per IP' },
    password_reset: { label: 'Password Reset', desc: 'Password reset requests per IP' },
    mfa_verify: { label: 'MFA Verification', desc: 'MFA code attempts per IP' },
    resend_verification: { label: 'Resend Verification', desc: 'Resend verification email requests per IP' },
  };

  const formatWindow = (seconds: number) => {
    if (seconds >= 3600) return `${Math.round(seconds / 3600)}h`;
    if (seconds >= 60) return `${Math.round(seconds / 60)}min`;
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <div className="d-flex flex-column min-vh-100 app-background">
        <Header />
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100 app-background">
      <Header />
      <div className="flex-grow-1">
        <Container className="py-3 py-md-5">
          <h1 className="display-5 fw-bold text-primary mb-4">Site Settings</h1>

          {/* Unread Ticket Notification */}
          {unreadTickets > 0 && (
            <Card 
              className="mb-4 border-0 shadow-sm" 
              style={{ background: 'linear-gradient(135deg, #92400e 0%, #d97706 100%)', cursor: 'pointer' }}
              onClick={() => navigate('/manage/support')}
            >
              <Card.Body className="d-flex align-items-center justify-content-between py-3 px-4">
                <div className="d-flex align-items-center gap-3">
                  <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '12px', padding: '10px' }}>
                    <TicketIcon style={{ width: '24px', height: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <h6 className="mb-0 text-white fw-bold">
                      {unreadTickets} company ticket{unreadTickets !== 1 ? 's' : ''} need{unreadTickets === 1 ? 's' : ''} your response
                    </h6>
                    <small style={{ color: 'rgba(255,255,255,0.8)' }}>Companies are waiting for admin support</small>
                  </div>
                </div>
                <Button variant="light" size="sm" className="fw-semibold" style={{ color: '#92400e' }}>
                  View Tickets
                </Button>
              </Card.Body>
            </Card>
          )}

          {success && <Alert variant="success" dismissible onClose={() => setFeedback(prev => ({ ...prev, success: '' }))}>{success}</Alert>}
          {error && <Alert variant="danger" dismissible onClose={() => setFeedback(prev => ({ ...prev, error: '' }))}>{error}</Alert>}

          <Tabs defaultActiveKey="seo" className="mb-4">
            {/* SEO Tab */}
            <Tab eventKey="seo" title="SEO & Meta Tags">
              <Card className="shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">Search Engine Optimization</h5>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Page Title <small className="text-muted">({metaTitle.length}/70)</small></Form.Label>
                      <Form.Control
                        type="text"
                        value={metaTitle}
                        onChange={(e) => setSeoFields(prev => ({ ...prev, metaTitle: e.target.value }))}
                        maxLength={70}
                        placeholder="e.g., Sourdoughsy - Premium Wholesale Bakery"
                      />
                      <Form.Text className="text-muted">Appears as the browser tab title and in search results</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Meta Description <small className="text-muted">({metaDescription.length}/160)</small></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={metaDescription}
                        onChange={(e) => setSeoFields(prev => ({ ...prev, metaDescription: e.target.value }))}
                        maxLength={160}
                        placeholder="Brief description for search engine results..."
                      />
                      <Form.Text className="text-muted">Shown under the title in Google search results</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Keywords</Form.Label>
                      <Form.Control
                        type="text"
                        value={metaKeywords}
                        onChange={(e) => setSeoFields(prev => ({ ...prev, metaKeywords: e.target.value }))}
                        placeholder="wholesale, bakery, sourdough, bread, pastries"
                      />
                      <Form.Text className="text-muted">Comma-separated keywords</Form.Text>
                    </Form.Group>

                    <hr />
                    <h6 className="mb-3">Social Sharing (Open Graph)</h6>

                    <Form.Group className="mb-3">
                      <Form.Label>OG Title <small className="text-muted">({ogTitle.length}/70)</small></Form.Label>
                      <Form.Control
                        type="text"
                        value={ogTitle}
                        onChange={(e) => setSeoFields(prev => ({ ...prev, ogTitle: e.target.value }))}
                        maxLength={70}
                        placeholder="Title shown when shared on social media"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>OG Description <small className="text-muted">({ogDescription.length}/200)</small></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={ogDescription}
                        onChange={(e) => setSeoFields(prev => ({ ...prev, ogDescription: e.target.value }))}
                        maxLength={200}
                        placeholder="Description shown when shared on Facebook, Twitter, etc."
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>OG Image URL</Form.Label>
                      <Form.Control
                        type="url"
                        value={ogImageUrl}
                        onChange={(e) => setSeoFields(prev => ({ ...prev, ogImageUrl: e.target.value }))}
                        placeholder="https://example.com/image.jpg"
                      />
                      <Form.Text className="text-muted">Recommended: 1200x630 pixels</Form.Text>
                    </Form.Group>

                    <hr />
                    <h6 className="mb-3">Advanced SEO</h6>

                    <Form.Group className="mb-3">
                      <Form.Label>Canonical URL</Form.Label>
                      <Form.Control
                        type="url"
                        value={canonicalUrl}
                        onChange={(e) => setSeoFields(prev => ({ ...prev, canonicalUrl: e.target.value }))}
                        placeholder="https://sourdoughsy.com"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Google Analytics ID</Form.Label>
                      <Form.Control
                        type="text"
                        value={googleAnalyticsId}
                        onChange={(e) => setSeoFields(prev => ({ ...prev, googleAnalyticsId: e.target.value }))}
                        placeholder="G-XXXXXXXXXX"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Google Tag Manager Container ID</Form.Label>
                      <Form.Control
                        type="text"
                        value={gtmContainerId}
                        onChange={(e) => setSeoFields(prev => ({ ...prev, gtmContainerId: e.target.value }))}
                        placeholder="GTM-XXXXXXX"
                      />
                      <Form.Text className="text-muted">Enables Google Tag Manager on all pages. Loaded after cookie consent.</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Robots.txt</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={robotsTxt}
                        onChange={(e) => setSeoFields(prev => ({ ...prev, robotsTxt: e.target.value }))}
                        style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                      />
                    </Form.Group>

                    <Button variant="primary" onClick={() => handleSave('seo')} disabled={saving}>
                      {saving ? 'Saving...' : 'Save SEO Settings'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* Branding Tab */}
            <Tab eventKey="branding" title="Site Branding">
              <Card className="shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">Site Branding</h5>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Site Name</Form.Label>
                      <Form.Control type="text" value={siteName} onChange={(e) => setBranding(prev => ({ ...prev, siteName: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Tagline</Form.Label>
                      <Form.Control type="text" value={siteTagline} onChange={(e) => setBranding(prev => ({ ...prev, siteTagline: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Site Description</Form.Label>
                      <Form.Control as="textarea" rows={3} value={siteDescription} onChange={(e) => setBranding(prev => ({ ...prev, siteDescription: e.target.value }))} />
                    </Form.Group>
                    <Button variant="primary" onClick={() => handleSave('branding')} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Branding'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* Company Tab */}
            <Tab eventKey="company" title="Company Info">
              <Card className="shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">Company Information</h5>
                  <Form>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Company Name</Form.Label>
                          <Form.Control type="text" value={companyName} onChange={(e) => setCompanyInfo(prev => ({ ...prev, companyName: e.target.value }))} />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control type="text" value={companyPhone} onChange={(e) => setCompanyInfo(prev => ({ ...prev, companyPhone: e.target.value }))} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email</Form.Label>
                          <Form.Control type="email" value={companyEmail} onChange={(e) => setCompanyInfo(prev => ({ ...prev, companyEmail: e.target.value }))} />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Website</Form.Label>
                          <Form.Control type="url" value={companyWebsite} onChange={(e) => setCompanyInfo(prev => ({ ...prev, companyWebsite: e.target.value }))} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label>Address</Form.Label>
                      <Form.Control as="textarea" rows={2} value={companyAddress} onChange={(e) => setCompanyInfo(prev => ({ ...prev, companyAddress: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Business Hours</Form.Label>
                      <Form.Control as="textarea" rows={2} value={businessHours} onChange={(e) => setCompanyInfo(prev => ({ ...prev, businessHours: e.target.value }))} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Welcome Message</Form.Label>
                      <Form.Control as="textarea" rows={2} value={welcomeMessage} onChange={(e) => setCompanyInfo(prev => ({ ...prev, welcomeMessage: e.target.value }))} />
                    </Form.Group>
                    <Button variant="primary" onClick={() => handleSave('company')} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Company Info'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* Social Media Tab */}
            <Tab eventKey="social" title="Social Media">
              <Card className="shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">Social Media Links</h5>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Facebook URL</Form.Label>
                      <Form.Control type="url" value={facebookUrl} onChange={(e) => setSocialLinks(prev => ({ ...prev, facebookUrl: e.target.value }))} placeholder="https://facebook.com/..." />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Instagram URL</Form.Label>
                      <Form.Control type="url" value={instagramUrl} onChange={(e) => setSocialLinks(prev => ({ ...prev, instagramUrl: e.target.value }))} placeholder="https://instagram.com/..." />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Twitter URL</Form.Label>
                      <Form.Control type="url" value={twitterUrl} onChange={(e) => setSocialLinks(prev => ({ ...prev, twitterUrl: e.target.value }))} placeholder="https://twitter.com/..." />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>LinkedIn URL</Form.Label>
                      <Form.Control type="url" value={linkedinUrl} onChange={(e) => setSocialLinks(prev => ({ ...prev, linkedinUrl: e.target.value }))} placeholder="https://linkedin.com/..." />
                    </Form.Group>
                    <Button variant="primary" onClick={() => handleSave('social')} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Social Media'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* Footer Tab */}
            <Tab eventKey="footer" title="Footer">
              <Card className="shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">Footer Content</h5>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Dashboard Footer Text</Form.Label>
                      <Form.Control type="text" value={footerText} onChange={(e) => setFooterFields(prev => ({ ...prev, footerText: e.target.value }))} placeholder="e.g., Wholesale Management System" />
                      <Form.Text className="text-muted">Shown on the right side of the customer & company dashboard footer</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Copyright Text</Form.Label>
                      <Form.Control type="text" value={footerCopyright} onChange={(e) => setFooterFields(prev => ({ ...prev, footerCopyright: e.target.value }))} placeholder="Leave blank to use company name" />
                      <Form.Text className="text-muted">Override the copyright line. Leave blank for auto: "© 2026 [Company Name]. All rights reserved."</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Public Footer Tagline</Form.Label>
                      <Form.Control as="textarea" rows={2} value={footerTagline} onChange={(e) => setFooterFields(prev => ({ ...prev, footerTagline: e.target.value }))} placeholder="e.g., Wholesale bakery management platform." />
                      <Form.Text className="text-muted">Short tagline shown in the public website footer (homepage, pricing, etc.)</Form.Text>
                    </Form.Group>
                    <Button variant="primary" onClick={() => handleSave('footer')} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Footer'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* Logos & Favicon Tab */}
            <Tab eventKey="logos" title="Logos & Favicon">
              {logosLoading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
              ) : (
                <>
                  {[
                    { type: 'favicon', label: 'Favicon', desc: 'Browser tab icon. Recommended: 32x32 or 48x48 PNG/ICO.' },
                    { type: 'main', label: 'Main Logo', desc: 'Used on the homepage and public pages.' },
                    { type: 'header', label: 'Header Logo', desc: 'Displayed in the navigation bar.' },
                    { type: 'login', label: 'Login Logo', desc: 'Shown on the login page.' },
                  ].map(({ type, label, desc }) => {
                    const active = getActiveLogo(type);
                    const all = getLogosByType(type);
                    return (
                      <Card className="shadow-sm mb-3" key={type}>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <h6 className="mb-1">{label}</h6>
                              <small className="text-muted">{desc}</small>
                            </div>
                            <Button size="sm" variant="outline-primary" onClick={() => openUploadModal(type)}>
                              Upload New
                            </Button>
                          </div>
                          {active ? (
                            <div className="d-flex align-items-center gap-3 mt-3 p-3 rounded" style={{ background: type === 'favicon' ? '#f8f9fa' : '#f8f9fa', border: '1px solid #dee2e6' }}>
                              <div style={{
                                width: type === 'favicon' ? '48px' : '120px',
                                height: type === 'favicon' ? '48px' : '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'white',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef',
                                padding: '4px'
                              }}>
                                <img
                                  src={active.image_url}
                                  alt={active.alt_text || active.name}
                                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                              </div>
                              <div className="flex-grow-1">
                                <div className="fw-medium">{active.name}</div>
                                <small className="text-muted">{active.alt_text}</small>
                                <div><Badge bg="success" className="mt-1">Active</Badge></div>
                              </div>
                              <Button size="sm" variant="outline-danger" onClick={() => setLogoState(prev => ({ ...prev, deleteConfirm: active.id }))}>
                                Delete
                              </Button>
                            </div>
                          ) : (
                            <div className="text-center py-4 mt-2 rounded" style={{ background: '#f8f9fa', border: '2px dashed #dee2e6' }}>
                              <div className="text-muted mb-2">No {label.toLowerCase()} set</div>
                              <Button size="sm" variant="primary" onClick={() => openUploadModal(type)}>
                                Upload {label}
                              </Button>
                            </div>
                          )}
                          {all.filter(l => !l.is_active).length > 0 && (
                            <div className="mt-2">
                              <small className="text-muted d-block mb-1">Inactive:</small>
                              {all.filter(l => !l.is_active).map(l => (
                                <div key={l.id} className="d-flex align-items-center gap-2 py-1">
                                  <img src={l.image_url} alt={l.alt_text} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                                  <small>{l.name}</small>
                                  <Button size="sm" variant="link" className="p-0 ms-auto" onClick={() => handleToggleActive(l)}>
                                    Set Active
                                  </Button>
                                  <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => setLogoState(prev => ({ ...prev, deleteConfirm: l.id }))}>
                                    Delete
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    );
                  })}
                </>
              )}
            </Tab>

            {/* Rate Limits Tab */}
            <Tab eventKey="ratelimits" title="Rate Limits">
              <Card className="shadow-sm">
                <Card.Body>
                  <h5 className="mb-1">Rate Limits</h5>
                  <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
                    Control how many attempts are allowed per IP address within a time window. Changes take effect within 5 minutes.
                  </p>
                  {Object.keys(RATE_LIMIT_LABELS).map(key => {
                    const cfg = rateLimits[key] || { max_attempts: 0, window_seconds: 0 };
                    const meta = RATE_LIMIT_LABELS[key];
                    return (
                      <Row key={key} className="mb-3 align-items-end">
                        <Col xs={12}>
                          <small className="fw-semibold text-uppercase text-muted" style={{ letterSpacing: '0.04em' }}>{meta.label}</small>
                          <div className="text-muted" style={{ fontSize: '0.82rem' }}>{meta.desc}</div>
                        </Col>
                        <Col xs={6} sm={4} md={3}>
                          <Form.Label className="mb-1" style={{ fontSize: '0.85rem' }}>Max Attempts</Form.Label>
                          <Form.Control
                            type="number" min={1} max={1000}
                            value={cfg.max_attempts}
                            onChange={e => setRateLimits(prev => ({
                              ...prev,
                              [key]: { ...prev[key], max_attempts: parseInt(e.target.value) || 1 },
                            }))}
                          />
                        </Col>
                        <Col xs={6} sm={4} md={3}>
                          <Form.Label className="mb-1" style={{ fontSize: '0.85rem' }}>Window (seconds)</Form.Label>
                          <Form.Control
                            type="number" min={10} max={86400}
                            value={cfg.window_seconds}
                            onChange={e => setRateLimits(prev => ({
                              ...prev,
                              [key]: { ...prev[key], window_seconds: parseInt(e.target.value) || 10 },
                            }))}
                          />
                        </Col>
                        <Col xs={12} sm={4} md={6} className="mt-1">
                          <Badge bg="light" text="dark" className="fw-normal" style={{ fontSize: '0.82rem' }}>
                            {cfg.max_attempts} attempt{cfg.max_attempts !== 1 ? 's' : ''} per {formatWindow(cfg.window_seconds)}
                          </Badge>
                        </Col>
                      </Row>
                    );
                  })}
                  <hr />
                  <Button variant="primary" onClick={handleSaveRateLimits} disabled={rateLimitsSaving}>
                    {rateLimitsSaving ? <><Spinner animation="border" size="sm" className="me-2" /> Saving...</> : 'Save Rate Limits'}
                  </Button>
                </Card.Body>
              </Card>
            </Tab>

            {/* Security Tab */}
            <Tab eventKey="security" title="Security">
              <Card className="mb-4">
                <Card.Body>
                  <h5 className="mb-1">Geo Restriction</h5>
                  <p className="text-muted small mb-3">Control which countries can access your site. When set to "USA Only", visitors from outside the United States will see a friendly block page.</p>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Site Visibility</Form.Label>
                    <div>
                      <Form.Check
                        type="radio"
                        id="geo-worldwide"
                        name="geoRestriction"
                        label={<><strong>Open to the World</strong> <span className="text-muted">— Anyone can access the site</span></>}
                        checked={geoRestriction === 'worldwide'}
                        onChange={() => setGeoRestriction('worldwide')}
                        className="mb-2"
                      />
                      <Form.Check
                        type="radio"
                        id="geo-usa"
                        name="geoRestriction"
                        label={<><strong>USA Only</strong> <span className="text-muted">— Block visitors from outside the United States</span></>}
                        checked={geoRestriction === 'usa_only'}
                        onChange={() => setGeoRestriction('usa_only')}
                      />
                    </div>
                  </Form.Group>
                  {geoRestriction === 'usa_only' && (
                    <Alert variant="info" className="small mb-3">
                      <strong>Note:</strong> Admin panel, API authentication, and local/private IPs are never blocked. If the GeoIP database is unavailable, all traffic is allowed as a safety fallback.
                    </Alert>
                  )}
                  <Button variant="primary" size="sm" onClick={() => handleSave('geo')} disabled={saving}>
                    {saving ? <><Spinner animation="border" size="sm" className="me-2" /> Saving...</> : 'Save Geo Settings'}
                  </Button>
                </Card.Body>
              </Card>
              <MFASetup />
            </Tab>

            {/* Legal / Terms Tab */}
            <Tab eventKey="legal" title="Legal" onEnter={() => { if (termsState.records.length === 0) loadTermsAcceptances(); }}>
              <TermsAcceptancesPanel termsState={termsState} loadTermsAcceptances={loadTermsAcceptances} setTermsState={setTermsState} />
            </Tab>

            {/* Maintenance Mode Tab */}
            <Tab eventKey="maintenance" title="Maintenance">
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">Maintenance Mode</h5>
                  <p className="text-muted small mb-4">When enabled, all non-admin users will see a maintenance page instead of the site.</p>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="maintenance-mode-switch"
                      label={maintenance.enabled ? 'Maintenance mode is ON' : 'Maintenance mode is OFF'}
                      checked={maintenance.enabled}
                      onChange={e => setMaintenance((prev: any) => ({ ...prev, enabled: e.target.checked }))}
                      style={{ fontSize: '1.05rem' }}
                    />
                    {maintenance.enabled && (
                      <div className="mt-1">
                        <small className="text-danger fw-semibold">⚠ Site is currently in maintenance mode. Only admins can access the site.</small>
                      </div>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Maintenance Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={maintenance.message}
                      onChange={e => setMaintenance((prev: any) => ({ ...prev, message: e.target.value }))}
                      placeholder="We are currently performing scheduled maintenance. Please check back soon."
                    />
                    <Form.Text className="text-muted">Displayed to users when maintenance mode is active</Form.Text>
                  </Form.Group>
                  <Button variant="primary" onClick={() => handleSave('maintenance')} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Maintenance Settings'}
                  </Button>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>

          {/* Upload Logo Modal */}
          <Modal show={showUploadModal} onHide={() => setUploadState(prev => ({ ...prev, showUploadModal: false }))} centered>
            <Modal.Header closeButton>
              <Modal.Title>Upload {uploadType === 'favicon' ? 'Favicon' : `${uploadType.charAt(0).toUpperCase() + uploadType.slice(1)} Logo`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control type="text" value={uploadName} onChange={(e) => setUploadState(prev => ({ ...prev, uploadName: e.target.value }))} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Alt Text</Form.Label>
                <Form.Control type="text" value={uploadAlt} onChange={(e) => setUploadState(prev => ({ ...prev, uploadAlt: e.target.value }))} placeholder="Descriptive text for accessibility" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Image File</Form.Label>
                <Form.Control type="file" accept="image/png,image/jpeg,image/svg+xml,image/gif,image/x-icon,image/vnd.microsoft.icon" ref={fileInputRef} onChange={handleFileSelect} />
                <Form.Text className="text-muted">
                  {uploadType === 'favicon' ? 'PNG, ICO, or SVG. Recommended 32x32 or 48x48px.' : 'PNG, JPG, SVG, or GIF.'}
                </Form.Text>
              </Form.Group>
              {uploadPreview && (
                <div className="text-center p-3 rounded" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                  <img src={uploadPreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }} />
                  <div className="mt-1"><small className="text-muted">Preview</small></div>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setUploadState(prev => ({ ...prev, showUploadModal: false }))}>Cancel</Button>
              <Button variant="primary" onClick={handleUploadLogo} disabled={uploading || !uploadFile}>
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal show={deleteConfirm !== null} onHide={() => setLogoState(prev => ({ ...prev, deleteConfirm: null }))} centered size="sm">
            <Modal.Body className="text-center py-4">
              <div className="mb-3" style={{ fontSize: '2rem' }}>🗑️</div>
              <p className="mb-0">Delete this logo permanently?</p>
            </Modal.Body>
            <Modal.Footer className="justify-content-center border-0 pt-0">
              <Button variant="outline-secondary" size="sm" onClick={() => setLogoState(prev => ({ ...prev, deleteConfirm: null }))}>Cancel</Button>
              <Button variant="danger" size="sm" onClick={() => deleteConfirm && handleDeleteLogo(deleteConfirm)}>Delete</Button>
            </Modal.Footer>
          </Modal>
        </Container>
      </div>
      <Footer />
    </div>
  );
};

/* Terms Acceptance Log Panel */
const TermsAcceptancesPanel: React.FC<{
  termsState: { records: any[]; total: number; page: number; loading: boolean; search: string };
  loadTermsAcceptances: (page?: number, search?: string) => void;
  setTermsState: React.Dispatch<React.SetStateAction<any>>;
}> = ({ termsState, loadTermsAcceptances, setTermsState }) => {
  const { records, total, page, loading: termsLoading, search: termsSearch } = termsState;
  const totalPages = Math.ceil(total / 25);

  useEffect(() => {
    loadTermsAcceptances(1, '');
  }, [loadTermsAcceptances]);

  return (
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <h5 className="mb-1">Terms &amp; Conditions Acceptance Log</h5>
        <p className="text-muted small mb-3">Records of user consent at registration</p>

        <InputGroup size="sm" className="mb-3" style={{ maxWidth: '360px' }}>
          <Form.Control
            placeholder="Search by name, email, or IP..."
            value={termsSearch}
            onChange={e => setTermsState((prev: any) => ({ ...prev, search: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter') loadTermsAcceptances(1, termsSearch); }}
          />
          <Button variant="outline-secondary" onClick={() => loadTermsAcceptances(1, termsSearch)}>Search</Button>
        </InputGroup>

        {termsLoading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
        ) : records.length === 0 ? (
          <p className="text-muted text-center py-4">No records found.</p>
        ) : (
          <>
            <div className="table-responsive">
              <Table hover size="sm" className="align-middle" style={{ fontSize: '0.84rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>User</th>
                    <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Company</th>
                    <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Accepted At</th>
                    <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>IP Address</th>
                    <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Version</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.id}>
                      <td>
                        <div className="fw-semibold">{r.username}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{r.email}</div>
                      </td>
                      <td>{r.company_name}</td>
                      <td>{new Date(r.accepted_at).toLocaleString()}</td>
                      <td><code style={{ fontSize: '0.8rem' }}>{r.ip_address}</code></td>
                      <td><Badge bg="info">{r.terms_version}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-2">
                <small className="text-muted">{total} total records</small>
                <Pagination size="sm" className="mb-0">
                  <Pagination.Prev disabled={page <= 1} onClick={() => loadTermsAcceptances(page - 1, termsSearch)} />
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const p = page <= 3 ? i + 1 : page - 2 + i;
                    if (p < 1 || p > totalPages) return null;
                    return <Pagination.Item key={p} active={p === page} onClick={() => loadTermsAcceptances(p, termsSearch)}>{p}</Pagination.Item>;
                  })}
                  <Pagination.Next disabled={page >= totalPages} onClick={() => loadTermsAcceptances(page + 1, termsSearch)} />
                </Pagination>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default AdminSEO;
